import NotebookRenderer from './NotebookRenderer';
import CodeBlockWithActions from './CodeBlockWithActions';

/**
 * Centralized markdown components factory for consistent rendering across all documentation
 * Supports: code highlighting, tables, images, links, Jupyter notebooks, and more
 *
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} - React Markdown components configuration
 */
export const createMarkdownComponents = (isDarkMode) => {
  return {
    code({ inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';

      // Handle Jupyter notebook rendering
      if (!inline && language === 'notebook') {
        return <NotebookRenderer content={String(children)} isDarkMode={isDarkMode} />;
      }

      // Handle code blocks with syntax highlighting and actions
      return !inline && language ? (
        <CodeBlockWithActions
          language={language}
          isDarkMode={isDarkMode}
          {...props}
        >
          {children}
        </CodeBlockWithActions>
      ) : (
        <code
          className={`${className} px-1.5 py-0.5 rounded text-sm font-mono ${
            isDarkMode
              ? 'bg-gray-800 text-gray-200 border border-gray-700'
              : 'bg-gray-100 text-gray-800 border border-gray-300'
          }`}
          {...props}
        >
          {children}
        </code>
      );
    },

    img({ src, alt }) {
      const altText = alt || '';
      const [actualAlt, sizeModifier] = altText.split('|');
      let imageStyle = {};
      if (sizeModifier) {
        if (sizeModifier.includes('%') || sizeModifier.includes('px')) {
          imageStyle.width = sizeModifier;
          imageStyle.maxWidth = sizeModifier;
        } else {
          imageStyle.width = `${sizeModifier}px`;
          imageStyle.maxWidth = `${sizeModifier}px`;
        }
      }
      return (
        <img
          src={src}
          alt={actualAlt}
          style={imageStyle}
          className="max-w-full h-auto rounded-lg shadow-md my-4 block"
        />
      );
    },

    a({ href, children, ...props }) {
      return (
        <a
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="text-primary-600 dark:text-primary-400 hover:underline break-words"
          {...props}
        >
          {children}
        </a>
      );
    },

    p({ children }) {
      return (
        <p className="mb-4 leading-relaxed text-ui-light-text-primary dark:text-ui-dark-text-primary break-words indent-6">
          {children}
        </p>
      );
    },

    ul({ children }) {
      return (
        <ul className="list-disc list-outside ml-6 mb-4 space-y-2 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {children}
        </ul>
      );
    },

    ol({ children }) {
      return (
        <ol className="list-decimal list-outside ml-6 mb-4 space-y-2 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {children}
        </ol>
      );
    },

    li({ children }) {
      return (
        <li className="break-words leading-relaxed pl-2">
          {children}
        </li>
      );
    },

    blockquote({ children }) {
      return (
        <blockquote className="border-l-4 border-ui-light-border dark:border-ui-dark-border pl-4 py-2 my-4 italic text-ui-light-text-secondary dark:text-ui-dark-text-tertiary overflow-hidden">
          {children}
        </blockquote>
      );
    },

    table({ children }) {
      return (
        <div className="overflow-x-auto my-4">
          <table className="min-w-full border-collapse border border-ui-light-border dark:border-ui-dark-border text-sm">
            {children}
          </table>
        </div>
      );
    },

    thead({ children }) {
      return (
        <thead className="bg-ui-light-background dark:bg-ui-dark-background">
          {children}
        </thead>
      );
    },

    th({ children }) {
      return (
        <th className="border border-ui-light-border dark:border-ui-dark-border px-4 py-2 text-left font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {children}
        </th>
      );
    },

    td({ children }) {
      return (
        <td className="border border-ui-light-border dark:border-ui-dark-border px-4 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {children}
        </td>
      );
    },

    hr() {
      return <hr className="my-6 border-ui-light-border dark:border-ui-dark-border" />;
    },

    h1({ children }) {
      return <h1 className="text-3xl font-bold mt-8 mb-4 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h1>;
    },

    h2({ children }) {
      return <h2 className="text-2xl font-bold mt-6 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h2>;
    },

    h3({ children }) {
      return <h3 className="text-xl font-bold mt-6 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h3>;
    },

    h4({ children }) {
      return <h4 className="text-lg font-bold mt-6 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h4>;
    },

    h5({ children }) {
      return <h5 className="text-base font-bold mt-6 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h5>;
    },

    h6({ children }) {
      return <h6 className="text-sm font-bold mt-6 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">{children}</h6>;
    }
  };
};
